import { createContext, useContext, useMemo } from "react";
import { useProducts } from "library/hooks/useProducts";
import { useAccounts } from "library/accounts";
import { useProfile } from "library/hooks/auth/useProfile";

const FaqContext = createContext();

function FaqProvider({ children }) {
  const { isLoggedIn } = useProfile();
  const accountsInfo = useAccounts({ enabled: isLoggedIn });
  const creditCards = accountsInfo?.data?.credit_cards;
  const personalLoans = accountsInfo?.data?.personal_loans;
  const productsInfo = useProducts({ enabled: isLoggedIn });
  const products = productsInfo.data;
  const value = useMemo(() => {
    if (accountsInfo.isLoading || productsInfo.isLoading) {
      return {
        isLoading: true,
        isError: false,
        data: undefined,
      };
    }

    if (accountsInfo.isError || productsInfo.isError) {
      return {
        isLoading: false,
        isError: true,
        data: undefined,
      };
    }

    const hasCreditCards = creditCards?.length > 0;
    const hasPersonalLoans = personalLoans?.length > 0;
    let hasAUaccounts = false;
    let hasNZaccounts = false;

    [...(creditCards ?? []), ...(personalLoans ?? [])].forEach((account) => {
      const product = products.find((p) => p.id === account.product_id);
      const region = product?.region;

      if (region === "AU") {
        hasAUaccounts = true;
      } else if (region === "NZ") {
        hasNZaccounts = true;
      }
    });

    return {
      isLoading: false,
      isError: false,
      data: {
        isLoggedIn,
        hasCreditCards,
        hasPersonalLoans,
        hasAUaccounts,
        hasNZaccounts,
      },
    };
  }, [
    isLoggedIn,
    accountsInfo.isLoading,
    accountsInfo.isError,
    creditCards,
    personalLoans,
    productsInfo.isLoading,
    productsInfo.isError,
    products,
  ]);

  return <FaqContext.Provider value={value}>{children}</FaqContext.Provider>;
}

function useFaqData() {
  return useContext(FaqContext);
}

export { FaqProvider, useFaqData };
